import { useCallback, useEffect, useMemo, useState } from 'react';
import locale, { Locales, BaseLocaleKey, LocaleObject } from 'locale/locale';
import tags from 'language-tags';

export type UseLocale = [LocaleObject, (value: Locales) => void, Locales];

/**
 * Hook to retrieve and update the locale
 * @returns The locale object containing all locale strings for the active locale,
 * the dispatcher to update the locale in local storage, and the value of the locale itself
 */
const useLocale = (userId: string | undefined): UseLocale => {
  // Language object to be used in components
  const [language, setLanguage] = useState<Locales>('English');
  // Construct the language key using the user's ID so that it's unique per user.
  const userLanguageKey = useMemo(() => (userId ? `${BaseLocaleKey}-${userId}` : undefined), [userId]);

  const setLocale = useCallback(
    (locale: Locales) => {
      if (userLanguageKey) {
        setLanguage(locale);

        window.localStorage.setItem(userLanguageKey, locale);
      }
    },
    [userLanguageKey],
  );

  useEffect(() => {
    if (userLanguageKey) {
      // Get the user's stored language
      const storedLanguage = window.localStorage.getItem(userLanguageKey) as Locales | null;

      // If we have one, use it...
      if (storedLanguage) {
        setLocale(storedLanguage);
        return;
      }

      // ...otherwise get the language descriptions (https://datatracker.ietf.org/doc/html/rfc5646#section-3.1.5)
      const languageDescriptions =
        tags(navigator.language || 'en-AU')
          ?.language()
          ?.descriptions() ?? [];

      // Get our available language keys
      const localeKeys = Object.keys(locale);

      // And try to match our descriptions with our language keys
      for (const languageDescription of languageDescriptions) {
        if (localeKeys.includes(languageDescription)) {
          setLocale(languageDescription as Locales);

          return;
        }

        continue;
      }

      // If not, fallback to English
      setLocale('English');
    }
    /* eslint-disable */
  }, [userLanguageKey]);

  return [locale[language], setLocale, language];
};

export default useLocale;
